import { FC, useEffect, useState } from 'react'
import {
    ColumnDef,
    createColumnHelper,
    Row,
    SortingState,
    Table,
} from '@atbay/cyber-components'
import { useDrawer } from '@core/context/DrawerContext'
import {
    StyledCustomTable,
    StyledTableHeader,
} from '@ui/components/CustomTable/CustomTable.styles'
import TableEmpty from '@ui/components/table/table-empty'
import Typography from '@ui/components/typography'
import { Cell } from './IssuesTable.styles'
import { IssuesTableProps } from './IssuesTable.types'
import { IssueStatus } from '@ui/components/IssueStatus'
import { SEVERITY_TEXT_MAPPING } from '@/ui/routes/digital-footprint/routes/assets/components/assetsTable/utils'
import { getTableRowContext } from '@/ui/utils/tableUtils'
import { SeverityCell } from '@/ui/routes/digital-footprint/routes/assets/components/assetsTable/assetsTable.styles'
import SeverityIcon from '@/ui/components/svg/SeverityIcon'
import { Analytics } from '@/core/services/mixpanel'
import { getIssueAnalyticsProps } from '@/core/modules/issues/analytics'
import { issueTypeToDrawerContentTypeMapping } from '@/core/context/DrawerContentTypes'
import { useIsIssueInProcess } from '@/core/hooks/useIsIssueInProcess'

const IssuesTable: FC<IssuesTableProps> = ({ issues }) => {
    const { setDrawerState, drawerState } = useDrawer()
    const [sorting, setSorting] = useState<SortingState>([])
    const columnHelper = createColumnHelper<TIssue>()
    const { checkIsIssueInProcess } = useIsIssueInProcess()
    const {
        isDrawerOpen,
        contentProps: { issues: sortedIssues, index: sortedIndex },
    } = drawerState

    const selectedIssue =
        isDrawerOpen && sortedIndex !== undefined
            ? sortedIssues?.[sortedIndex]
            : undefined

    const activeIssueId = selectedIssue?.id

    const getAssetCount = ({
        issue_type,
        assets,
        devices,
        technologies,
    }: TIssue): number => {
        return issue_type === 'INBOX_RULE'
            ? 1
            : (assets?.length ?? 0) +
                  (devices?.length ?? 0) +
                  (technologies?.length ?? 0)
    }

    useEffect(() => {
        if (!selectedIssue) return

        setDrawerState((prev) => ({
            ...prev,
            drawerContentType:
                issueTypeToDrawerContentTypeMapping[
                    selectedIssue.issue_type as keyof typeof issueTypeToDrawerContentTypeMapping
                ],
        }))
    }, [selectedIssue, setDrawerState])

    const handleRowClick = (row: Row<TIssue>, table: Table<TIssue>) => {
        const { sortedIndex, items } = getTableRowContext(row, table)
        const { issue_type } = row.original

        Analytics.event('SEM::user_clicks_at_issue', {
            ...getIssueAnalyticsProps(row.original),
            origin: 'my_stance_issues_table',
        })

        setDrawerState((prev) => ({
            ...prev,
            isDrawerOpen: true,
            drawerContentType: issueTypeToDrawerContentTypeMapping[issue_type],
            contentProps: {
                issues: items,
                index: sortedIndex,
            },
        }))
    }

    // Update drawer state to trigger re-render with new content type
    useEffect(() => {
        if (
            drawerState.isDrawerOpen &&
            drawerState.contentProps?.index !== undefined
        ) {
            const selectedIndex = drawerState.contentProps.index
            const selectedIssue = issues[drawerState.contentProps.index]

            if (selectedIssue) {
                const newDrawerContentType =
                    issueTypeToDrawerContentTypeMapping[
                        selectedIssue.issue_type
                    ]

                setDrawerState((prevState) => ({
                    ...prevState,
                    drawerContentType: newDrawerContentType,
                    contentProps: { issues, index: selectedIndex },
                }))
            }
        }
    }, [issues, drawerState.contentProps?.index, setDrawerState])

    const columns: ColumnDef<TIssue>[] = [
        {
            ...columnHelper.accessor('severity', {
                cell: (info) => (
                    <SeverityCell
                        data-testid="severity-cell"
                        onClick={() => handleRowClick(info.row, info.table)}
                    >
                        <SeverityIcon severity={info.cell.getValue()} />
                        <Typography
                            className="table-cell-text"
                            variant="s-regular"
                            testId="highest-severity"
                        >
                            {
                                SEVERITY_TEXT_MAPPING[
                                    info.cell.getValue() as TIssueSeverity
                                ]
                            }
                        </Typography>
                    </SeverityCell>
                ),
                header: () => (
                    <StyledTableHeader className="table-cell-text">
                        Severity
                    </StyledTableHeader>
                ),
                enableSorting: true,
            }),
            width: '20%',
        },
        {
            ...columnHelper.accessor('issue_category', {
                cell: (info) => (
                    <Cell
                        data-testid={`issue-type-${info.row.original.issue_type.toLowerCase()}`}
                        onClick={() => handleRowClick(info.row, info.table)}
                    >
                        <Typography className="table-cell-text capitalize">
                            {info.cell.getValue()?.split('_').join(' ')}
                        </Typography>
                    </Cell>
                ),
                header: () => (
                    <StyledTableHeader className="table-cell-text">
                        Issue Type
                    </StyledTableHeader>
                ),
            }),
            width: '20%',
        },
        {
            ...columnHelper.accessor('name', {
                cell: (info) => (
                    <Cell
                        data-testid="issue-name"
                        onClick={() => handleRowClick(info.row, info.table)}
                    >
                        <Typography className="table-cell-text">
                            {info.cell.getValue()}
                        </Typography>
                    </Cell>
                ),
                header: () => (
                    <StyledTableHeader className="table-cell-text">
                        Issue Name
                    </StyledTableHeader>
                ),
            }),
            width: '40%',
        },
        {
            ...columnHelper.accessor('assets', {
                cell: (info) => (
                    <Cell
                        data-testid="issue-assets-count"
                        onClick={() => handleRowClick(info.row, info.table)}
                    >
                        <Typography className="table-cell-text">
                            {getAssetCount(info.row.original)} assets
                        </Typography>
                    </Cell>
                ),
                header: () => (
                    <StyledTableHeader className="table-cell-text">
                        Asset Count
                    </StyledTableHeader>
                ),
                enableSorting: true,
                sortingFn: (a, b) =>
                    getAssetCount(a.original) - getAssetCount(b.original),
            }),
            width: '20%',
        },
        {
            ...columnHelper.accessor('status', {
                cell: (info) => (
                    <Cell
                        data-testid="issue-status"
                        onClick={() => handleRowClick(info.row, info.table)}
                    >
                        <IssueStatus
                            status={info.cell.getValue() as TIssueStatus}
                            className="table-cell-text"
                            isInProcess={checkIsIssueInProcess(
                                info.row.original.id
                            )}
                        />
                    </Cell>
                ),
                header: () => (
                    <StyledTableHeader className="table-cell-text">
                        Status
                    </StyledTableHeader>
                ),
                enableSorting: true,
            }),
            width: '20%',
        },
    ]

    return (
        <StyledCustomTable
            activeRow={activeIssueId}
            getRowId={(row: TIssue) => row.id}
            tableHeight={issues.length ? 800 : 400}
            columns={columns}
            data={issues}
            sorting={sorting}
            setSorting={setSorting}
            emptyState={<TableEmpty message="No issues." />}
        />
    )
}

export default IssuesTable
